import { BENCHMARKS_URL, ME_URL } from "../constants";
import { apiSlice } from "./apiSlice";

export const benchmarksApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getBenchmarks: builder.query({
      // `mine: true` lists the signed-in user's benchmarks (/api/me/benchmarks)
      // with the same paging/sort/filter params as the public list
      query: ({ mine, userId, page = 1, pageSize = 15, profile, clientVersion, contributor, sortBy, includeModified }) => {
        const params = new URLSearchParams({ page, pageSize });
        if (profile && profile !== 'All') params.set('profile', profile);
        if (clientVersion && clientVersion !== 'All') params.set('clientVersion', clientVersion);
        if (!mine && contributor && contributor !== 'All') params.set('contributor', contributor);
        if (sortBy && sortBy !== 'startTime_desc') params.set('sortBy', sortBy);
        // Server excludes modified-profile runs unless this is set
        if (includeModified) params.set('includeModified', 'true');

        if (mine) {
          return { url: `${ME_URL}/benchmarks?${params}` };
        }
        if (userId) {
          return { url: `${BENCHMARKS_URL}/user/${userId}/all_benchmarks?${params}` };
        }
        return { url: `${BENCHMARKS_URL}/all_benchmarks?${params}` };
      },
      // inherits global 300s default from apiSlice — list data is safe to cache
      providesTags: ["Benchmark"],
    }),
    getBenchmarkById: builder.query({
      query: ({ userId, benchmarkId }) => {
        if (userId) {
          return {
            url: `${BENCHMARKS_URL}/user/${userId}/all_benchmarks/benchmark/${benchmarkId}`,
          };
        }
        return {
          url: `${BENCHMARKS_URL}/all_benchmarks/benchmark/${benchmarkId}`,
        };
      },
      keepUnusedDataFor: 3600, // benchmarks are immutable after submission — cache for 1 hour
      providesTags: ["Benchmark"],
    }),
    deleteBenchmark: builder.mutation({
      query: ({ userId, benchmarkId }) => ({
        url: `${BENCHMARKS_URL}/user/${userId}/my_benchmarks/benchmark/${benchmarkId}/delete_benchmark`,
        method: "DELETE",
      }),
      // keepUnusedDataFor has no effect on mutations — removed
      invalidatesTags: ["Benchmark"],
    }),
    deleteMyBenchmark: builder.mutation({
      query: ({ benchmarkId }) => ({
        url: `${ME_URL}/benchmarks/benchmark/${benchmarkId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Benchmark'],
    }),
    bulkDeleteByVersion: builder.mutation({
      query: ({ clientVersion }) => ({
        url: `${BENCHMARKS_URL}/admin/bulk_delete?clientVersion=${encodeURIComponent(clientVersion)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Benchmark'],
    }),
    claimBenchmarks: builder.mutation({
      query: ({ systemId }) => ({
        url: `${ME_URL}/systems/claim`,
        method: 'POST',
        body: { systemId },
      }),
      invalidatesTags: ["Benchmark"],
    }),
    getMyClaimedSystems: builder.query({
      query: () => `${ME_URL}/systems`,
      keepUnusedDataFor: 5,
      providesTags: ["Benchmark"],
    }),
    getMySystemsDetail: builder.query({
      query: () => `${ME_URL}/systems/detail`,
      keepUnusedDataFor: 5,
      providesTags: ["Benchmark"],
    }),
  }),
});

export const {
  useGetBenchmarksQuery,
  useGetBenchmarkByIdQuery,
  useDeleteBenchmarkMutation,
  useClaimBenchmarksMutation,
  useGetMyClaimedSystemsQuery,
  useGetMySystemsDetailQuery,
  useDeleteMyBenchmarkMutation,
  useBulkDeleteByVersionMutation,
} = benchmarksApiSlice;